import { cn } from '@/platform/lib/utils';
import {
    CloseButton,
    Popover,
    PopoverButton,
    PopoverPanel,
} from '@headlessui/react';
import {
    CheckIcon,
    ChevronDownIcon,
    XMarkIcon,
} from '@heroicons/react/20/solid';
import Checkbox from '../Checkbox';

const sameId = (a, b) => String(a) === String(b);

const FilterBarDropdown = ({
    label,
    anchor = 'bottom start',
    options = [],
    multiple = false,
    clearable = false,
    selectAll = false,
    value,
    onChange,
    className,
    children,
    ...props
}) => {
    const selectedValues = multiple ? (value ?? []) : value;

    const isSelected = id =>
        multiple
            ? selectedValues.some(selected => sameId(selected, id))
            : sameId(selectedValues, id);

    const hasSelection = multiple
        ? selectedValues.length > 0
        : selectedValues !== '' && selectedValues != null;

    const toggle = id =>
        onChange?.(
            isSelected(id)
                ? selectedValues.filter(selected => !sameId(selected, id))
                : [...selectedValues, id]
        );

    const allSelected =
        options.length > 0 && options.every(option => isSelected(option.id));

    const toggleAll = () =>
        onChange?.(allSelected ? [] : options.map(option => option.id));

    return (
        <Popover className='relative'>
            <PopoverButton
                className={cn(
                    'inline-flex h-12 cursor-pointer items-center gap-1.5 px-4 text-sm font-medium text-gray-700 transition outline-none hover:bg-gray-50 data-open:bg-gray-50',
                    className
                )}
                {...props}
            >
                {label}
                <ChevronDownIcon className='size-4 text-gray-400' />
            </PopoverButton>
            <PopoverPanel
                transition
                anchor={anchor}
                className={cn(
                    'z-50 mt-1 flex min-w-48 origin-top flex-col rounded-md bg-white p-1 shadow-lg outline-1 outline-gray-900/15 transition [--anchor-max-height:16rem]',
                    'data-closed:scale-95 data-closed:opacity-0',
                    'data-enter:duration-200 data-enter:ease-out data-leave:duration-75 data-leave:ease-in'
                )}
            >
                {multiple && selectAll && options.length > 0 && (
                    <Checkbox
                        label={
                            typeof selectAll === 'string'
                                ? selectAll
                                : 'Alles selecteren'
                        }
                        checked={allSelected}
                        indeterminate={!allSelected && hasSelection}
                        onChange={toggleAll}
                        className='-mx-1 -mt-1 mb-1 border-b border-gray-900/10 px-4 py-1.5 transition hover:bg-gray-50'
                    />
                )}
                {options.map(option =>
                    multiple ? (
                        <Checkbox
                            key={option.id}
                            label={option.name}
                            checked={isSelected(option.id)}
                            onChange={() => toggle(option.id)}
                            className='rounded px-3 py-0.5 transition hover:bg-gray-50'
                        />
                    ) : (
                        <CloseButton
                            key={option.id}
                            onClick={() => onChange?.(option.id)}
                            className='flex cursor-pointer items-center justify-between gap-4 rounded px-3 py-1 text-left text-sm/6 font-medium text-gray-900 capitalize transition hover:bg-gray-50'
                        >
                            {option.name}
                            {isSelected(option.id) && (
                                <CheckIcon className='text-primary-600 size-4 shrink-0' />
                            )}
                        </CloseButton>
                    )
                )}
                {children}
                {clearable && hasSelection && (
                    <CloseButton
                        onClick={() => onChange?.(multiple ? [] : '')}
                        className='sticky bottom-0 z-10 -mx-1 mt-1 -mb-1 flex cursor-pointer items-center gap-1.5 rounded-b-md border-t border-gray-900/10 bg-white px-4 py-1.5 text-left text-sm/6 font-medium text-gray-500 transition hover:bg-gray-50'
                    >
                        <XMarkIcon className='size-4 shrink-0' />
                        Wissen
                    </CloseButton>
                )}
            </PopoverPanel>
        </Popover>
    );
};

export default FilterBarDropdown;
